import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const schema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Mínimo 6 caracteres'),
});

type FormData = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const [showPass, setShowPass] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Redireciona quando AuthProvider confirmar a sessão + profile carregado
  useEffect(() => {
    if (!loading && session) navigate('/', { replace: true });
  }, [session, loading, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async ({ email, password }: FormData) => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setAuthError('E-mail ou senha incorretos. Verifique suas credenciais.');
    }
    // Não navega aqui — o useEffect acima faz isso quando loading=false + session pronto
  };

  return (
    <div className="flex min-h-screen">
      {/* Painel esquerdo — Navy */}
      <div className="hidden flex-col justify-between bg-primary p-12 lg:flex lg:w-1/2">
        <img
          src="/identidade-visual/logo/vetor/logo-horizontal-branco.svg"
          alt="Clinance"
          className="h-10 w-auto"
        />
        <div>
          <h1 className="mb-4 font-brand text-4xl font-bold leading-tight text-white">
            BPO Financeiro para clínicas odontológicas
          </h1>
          <p className="text-lg text-white/70">
            Gestão financeira profissional para que você foque no que importa: seus pacientes.
          </p>
        </div>
        <p className="text-sm text-white/40">© {new Date().getFullYear()} Clinance</p>
      </div>

      {/* Painel direito — Formulário */}
      <div className="flex flex-1 items-center justify-center bg-secondary p-8">
        <div className="w-full max-w-md">
          {/* Logo mobile */}
          <div className="mb-8 flex justify-center lg:hidden">
            <img
              src="/identidade-visual/logo/vetor/logo-horizontal.svg"
              alt="Clinance"
              className="h-10 w-auto"
            />
          </div>

          <div className="rounded-2xl border border-border bg-white p-8 shadow-sm">
            <h2 className="mb-2 font-brand text-2xl font-bold text-foreground">Entrar</h2>
            <p className="mb-8 text-sm text-muted-foreground">
              Use suas credenciais para acessar o sistema.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="seu@email.com.br"
                  {...register('email')}
                />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPass ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="pr-10"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password.message}</p>
                )}
              </div>

              {authError && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {authError}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Entrando…
                  </>
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/reset-password"
                className="text-sm text-primary transition-colors hover:text-primary/80"
              >
                Esqueceu sua senha?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
